import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, CircularProgress } from '@mui/material';
import QRCodeStyling from 'qr-code-styling';
import funzoneLogo from '../../assets/logos/funzone.webp';

/**
 * Reusable FunZone-styled QR component for management frontend.
 * Mirrors customer-frontend styling and behavior.
 */
const FunzoneStyledQr = forwardRef(function FunzoneStyledQr(
  {
    data,
    size = 360,
    centerLogoSrc,
    backgroundColor = '#FFFF00',
    maxSize = 360,
    margin = 8,
    readyDelayMs = 250,
  },
  ref
) {
  const containerRef = useRef(null);
  const outerRef = useRef(null);
  const instanceRef = useRef(null);
  const renderSizeRef = useRef(size);
  const [ready, setReady] = useState(false);
  const [computedSize, setComputedSize] = useState(size);

  const rawImageSrc = useMemo(() => centerLogoSrc || funzoneLogo, [centerLogoSrc]);
  const [resolvedImageSrc, setResolvedImageSrc] = useState(null);

  // resolve svg to data url; if svg, rasterize to png to ensure reliable embedding
  useEffect(() => {
    let cancelled = false;
    const src = rawImageSrc;
    const resolve = async () => {
      try {
        if (!src) {
          setResolvedImageSrc(null);
          return;
        }
        if (typeof src === 'string' && src.endsWith('.svg')) {
          const resp = await fetch(src);
          const text = await resp.text();
          const encoded = encodeURIComponent(text).replace(/%0A/g, '').replace(/%20/g, ' ');
          const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encoded}`;
          const pngDataUrl = await new Promise(resolvePng => {
            try {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                const width = Math.max(256, img.naturalWidth || 512);
                const height = Math.max(256, img.naturalHeight || 512);
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                  resolvePng(svgDataUrl);
                  return;
                }
                ctx.clearRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);
                try {
                  resolvePng(canvas.toDataURL('image/png'));
                } catch {
                  resolvePng(svgDataUrl);
                }
              };
              img.onerror = () => resolvePng(svgDataUrl);
              img.src = svgDataUrl;
            } catch {
              resolvePng(svgDataUrl);
            }
          });
          if (!cancelled) setResolvedImageSrc(pngDataUrl);
        } else {
          setResolvedImageSrc(src);
        }
      } catch {
        setResolvedImageSrc(src);
      }
    };
    resolve();
    return () => {
      cancelled = true;
    };
  }, [rawImageSrc]);

  const fitAppendedNode = () => {
    const container = containerRef.current;
    if (!container) return;
    const child = container.firstElementChild;
    if (!child) return;
    try {
      child.style.width = '100%';
      child.style.height = '100%';
      child.style.display = 'block';
      if (child.tagName && child.tagName.toLowerCase() === 'svg') {
        child.setAttribute('width', '100%');
        child.setAttribute('height', '100%');
        child.style.maxWidth = '100%';
        child.style.maxHeight = '100%';
      }
    } catch {}
  };

  // initialize QR instance once and attach to container
  useEffect(() => {
    if (!instanceRef.current) {
      instanceRef.current = new QRCodeStyling({
        width: renderSizeRef.current,
        height: renderSizeRef.current,
        type: 'svg',
        data: '',
        image: resolvedImageSrc || undefined,
        margin: margin,
        qrOptions: { errorCorrectionLevel: 'H' },
        // start with transparent background to avoid flashing color before ready
        backgroundOptions: { color: 'transparent' },
        dotsOptions: {
          type: 'dots',
          gradient: {
            type: 'linear',
            rotation: 0,
            colorStops: [
              { offset: 0, color: '#111827' },
              { offset: 1, color: '#1f2937' },
            ],
          },
        },
        cornersSquareOptions: { color: '#111827', type: 'extra-rounded' },
        cornersDotOptions: { color: '#111827', type: 'dot' },
        imageOptions: {
          margin: 2,
          imageSize: 0.3,
          hideBackgroundDots: true,
          crossOrigin: 'anonymous',
        },
      });
    }
    if (containerRef.current && instanceRef.current) {
      try {
        containerRef.current.innerHTML = '';
      } catch {}
      instanceRef.current.append(containerRef.current);
      fitAppendedNode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // update QR when data or logo changes
  useEffect(() => {
    if (!instanceRef.current) return;
    setReady(false);
    try {
      if (containerRef.current) {
        try {
          containerRef.current.innerHTML = '';
        } catch {}
        instanceRef.current.append(containerRef.current);
        fitAppendedNode();
      }
      // First update: render data with transparent background to keep loader visible
      instanceRef.current.update({
        data: data || '',
        image: resolvedImageSrc || undefined,
        imageOptions: {
          margin: 2,
          imageSize: 0.3,
          hideBackgroundDots: true,
          crossOrigin: 'anonymous',
        },
        backgroundOptions: { color: 'transparent' },
      });
      if (data) {
        requestAnimationFrame(() => {
          setTimeout(
            () => {
              setReady(true);
              try {
                instanceRef.current &&
                  instanceRef.current.update({
                    backgroundOptions: { color: backgroundColor },
                    margin: margin,
                  });
              } catch {}
            },
            Math.max(0, readyDelayMs)
          );
        });
      }
    } catch {
      setReady(false);
    }
  }, [data, resolvedImageSrc]);

  // observe container width to make QR responsive
  useEffect(() => {
    const updateSize = () => {
      const width = (outerRef.current && outerRef.current.clientWidth) || size;
      const next = Math.max(80, Math.min(width, maxSize));
      setComputedSize(next);
    };
    updateSize();
    let ro = null;
    try {
      ro = new ResizeObserver(() => updateSize());
      if (outerRef.current) ro.observe(outerRef.current);
    } catch {
      window.addEventListener('resize', updateSize);
    }
    return () => {
      if (ro && outerRef.current) ro.unobserve(outerRef.current);
      window.removeEventListener('resize', updateSize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxSize]);

  // update underlying QR size when our computed size changes
  useEffect(() => {
    if (!instanceRef.current) return;
    try {
      instanceRef.current.update({
        width: Math.round(computedSize),
        height: Math.round(computedSize),
      });
      fitAppendedNode();
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedSize]);

  useImperativeHandle(ref, () => ({
    download: (fileName, extension = 'png') => {
      if (!instanceRef.current) return;
      return instanceRef.current.download({ name: fileName || 'funzone-qr', extension });
    },
  }));

  return (
    <Box
      ref={outerRef}
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: maxSize,
          aspectRatio: '1 / 1',
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'transparent',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        >
          <CircularProgress
            size={72}
            thickness={4}
            sx={{ color: 'primary.main', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.35))' }}
          />
        </Box>
        <div
          ref={containerRef}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: ready ? 1 : 0,
            zIndex: 1,
          }}
        />
      </Box>
    </Box>
  );
});

export default FunzoneStyledQr;
